/**
 * Shared legal-diary helpers: traffic-light priority rendering + the
 * event chip style maps used by the Legal Calendar, the per-case Legal
 * Schedule tab and the Legal Case Tracker.
 *
 * Priority is computed SERVER-SIDE (list endpoints annotate each
 * scheduled event with `priority: 'red'|'amber'|'green'|null` +
 * `days_overdue`) using the TL-configurable thresholds in Portal
 * Settings → Legal Diary. These helpers only render it.
 */

// Event-type tone → Tailwind chip. Keep names in sync with backend
// LEGAL_EVENT_TYPES tones.
export const TONE_CLASS = {
  amber:   "bg-amber-100   text-amber-900   border-amber-300",
  red:     "bg-red-100     text-red-900     border-red-300",
  orange:  "bg-orange-100  text-orange-900  border-orange-300",
  indigo:  "bg-indigo-100  text-indigo-900  border-indigo-300",
  blue:    "bg-blue-100    text-blue-900    border-blue-300",
  violet:  "bg-violet-100  text-violet-900  border-violet-300",
  rose:    "bg-rose-100    text-rose-900    border-rose-300",
  slate:   "bg-slate-100   text-slate-700   border-slate-300",
};

export const STATUS_TONE = {
  scheduled: "bg-blue-50  text-blue-700  border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  missed:    "bg-red-50    text-red-700     border-red-200",
  cancelled: "bg-slate-100 text-slate-500   border-slate-200",
};

export const PRIORITY_META = {
  green: { dot: "bg-emerald-500", ring: "ring-emerald-200", label: "Due today" },
  amber: { dot: "bg-amber-400",   ring: "ring-amber-200",   label: "Overdue" },
  red:   { dot: "bg-red-600",     ring: "ring-red-200",     label: "Overdue — high priority" },
};

// Event types where attaching a specific court makes sense — drives the
// optional Court section in AddLegalEventDialog.
export const COURT_EVENT_TYPES = new Set([
  "court_hearing", "petition_filed", "winding_up_petition", "chase_court",
  "can_petition", "bankruptcy_order", "ccj_obtained",
  "charging_order_interim", "charging_order_final",
]);

// Traffic-light dot. `priority` comes straight off the API event/case row.
export function PriorityDot({ priority, daysOverdue, className = "" }) {
  if (!priority || !PRIORITY_META[priority]) return null;
  const meta = PRIORITY_META[priority];
  const title = priority === "green"
    ? "Due today"
    : `${meta.label} — ${daysOverdue} day${daysOverdue === 1 ? "" : "s"} overdue`;
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ring-2 ${meta.dot} ${meta.ring} ${className}`}
      title={title}
      data-testid={`priority-dot-${priority}`}
    />
  );
}

// Compact one-line court summary, e.g. "Central London County Court · DX 44453 Strand"
export function courtSummary(court) {
  if (!court?.name) return "";
  const bits = [court.name];
  if (court.dx_number) bits.push(`DX ${court.dx_number}`);
  return bits.join(" · ");
}
